import React from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { BASE_API_URL } from "store/Me";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { CATEGORY_LABEL, ImpactSummary, SiteSyncRecordImpact, statusColor, SyncTypesLabel } from "components/shared/siteSyncRecordShared";
import { Badge } from "components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { statusBadgeOutlineClass } from "shared/utils/statusChipColor";
import { SITE_SYNC_RECORDS_QUERY_KEY } from "./siteQueryKeys";

interface SiteSyncRecord {
  id: string;
  siteId?: string;
  timestamp: string;
  completedTimestamp?: string;
  requestedByUserId?: string;
  requestedByName?: string;
  triggerType: string;
  triggerName?: string;
  category: string;
  status: string;
  types: string;
  error?: string;
  impacts: SiteSyncRecordImpact[];
}

async function getSiteSyncRecords(
  siteId: string,
  page: number,
  pageSize: number,
): Promise<SiteSyncRecord[]> {
  const urlParams = new URLSearchParams();
  urlParams.append("page", page.toString());
  urlParams.append("pageSize", pageSize.toString());
  const response = await axios.get<SiteSyncRecord[]>(
    `${BASE_API_URL}/api/Sites/${siteId}/sync-records?${urlParams.toString()}`,
  );
  return response.data;
}

function useGetSiteSyncRecords(siteId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: [SITE_SYNC_RECORDS_QUERY_KEY, siteId, page, pageSize],
    queryFn: () => getSiteSyncRecords(siteId, page, pageSize),
  });
}

export default function SiteSyncRecords({ siteId }: { siteId: string }) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  const { data, isLoading } = useGetSiteSyncRecords(siteId, page, pageSize);
  const records = data ?? [];

  if (isLoading) {
    return (
      <div className="flex min-h-80 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm text-slate-500 shadow-sm">
        Loading sync records...
      </div>
    );
  }

  return (
    <AdminTable
      aria-label="Site sync records"
      footer={
        <AdminTablePagination
          currentPage={page}
          pageSize={pageSize}
          visibleRowCount={records.length}
          isLoading={isLoading}
          onNext={() => setPage((currentPage) => currentPage + 1)}
          onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          disablePrevious={page === 0}
          disableNext={records.length < pageSize}
        />
      }
    >
        <thead>
          <tr>
            <th scope="col">Timestamp</th>
            <th scope="col">Completed</th>
            <th scope="col">Category</th>
            <th scope="col">Trigger</th>
            <th scope="col">Operations</th>
            <th scope="col">Status</th>
            <th scope="col">Impact</th>
            <th scope="col">Error</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={record.id}>
              <td style={{ whiteSpace: "nowrap" }}>
                {new Date(record.timestamp).toLocaleString()}
                {record.requestedByName && (
                  <div style={{ fontSize: 11, color: "gray" }}>{record.requestedByName}</div>
                )}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                {record.completedTimestamp
                  ? new Date(record.completedTimestamp).toLocaleString()
                  : "—"}
              </td>
              <td>{CATEGORY_LABEL[record.category] ?? record.category}</td>
              <td>
                {record.triggerName ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default" tabIndex={0}>
                        {record.triggerType}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">{record.triggerName}</TooltipContent>
                  </Tooltip>
                ) : (
                  record.triggerType
                )}
              </td>
              <td>
                <SyncTypesLabel types={record.types} />
              </td>
              <td>
                <Badge
                  className={statusBadgeOutlineClass(statusColor(record.status))}
                  variant="outline"
                >
                  {record.status}
                </Badge>
              </td>
              <td>
                <ImpactSummary impacts={record.impacts} />
              </td>
              <td style={{ color: record.error ? "red" : undefined, maxWidth: 200, fontSize: 12 }}>
                {record.error ?? "—"}
              </td>
            </tr>
          ))}
          {records.length === 0 && (
            <tr>
              <td colSpan={8} className="py-16 text-center text-sm text-slate-500">
                No sync records found.
              </td>
            </tr>
          )}
        </tbody>
    </AdminTable>
  );
}
